/**
 * MBA Lab — pure model for the Team Time-Zone Overlap Planner.
 *
 * PURITY IS THE POINT OF THIS FILE. It contains zero `Intl`, zero DOM, and
 * zero `Date.now()` / `new Date()`. Every function here takes a **precomputed
 * `offsetMinutes`** per member and does all of its arithmetic on a 48-slot
 * half-hour UTC grid. That seam is what makes the whole tool testable without
 * a clock or a locale: `lib/timezone-zones.js` is the only module allowed to
 * touch `Intl`, and the UI passes the numbers it resolves down into here.
 *
 * Half-hour slots are not an arbitrary choice - India is UTC+05:30, so an
 * hourly grid cannot represent a real Indian working day at all. Zones with a
 * 45-minute offset (Kathmandu, Chatham) are the one case the grid cannot
 * express exactly; `bandToSlots` snaps them INWARD (start rounds up, end
 * rounds down) so the tool under-claims availability rather than promising a
 * quarter hour that isn't there.
 *
 * The advice this operationalizes comes from
 * Content/Blog/global-cohort-time-zones.md - the rotation, exemption, and
 * handoff language below deliberately mirrors that article rather than
 * inventing a parallel method.
 */

import { escapeMarkdown } from './charter-model.js';

export const SCHEMA_NAME = 'mbalab.team-time-zone-planner';
export const SCHEMA_VERSION = 1;

export const SLOT_MINUTES = 30;
export const SLOTS_PER_DAY = 48;
export const MINUTES_PER_DAY = 1440;
export const MINUTES_PER_WEEK = 10080;
export const MAX_MEMBERS = 8;

export const WEEKDAYS = Object.freeze([
  'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday',
]);

/** 09:00-17:00 local. A core day, not the outer window people will stretch to. */
export const DEFAULT_WORK_START_MIN = 540;
export const DEFAULT_WORK_END_MIN = 1020;

/**
 * The "stretch" band is the wider window a person will genuinely accept for
 * team work, and it is deliberately asymmetric: almost nobody treats an hour
 * early and an hour late as equally cheap. One hour before and four hours
 * after a 09:00-17:00 core day produces exactly the 08:00-21:00 / 13-hour
 * acceptance window the source article's arithmetic assumes.
 */
export const STRETCH_EARLY_MINUTES = 60;
export const STRETCH_LATE_MINUTES = 240;

/** All multiples of SLOT_MINUTES so a meeting always lands on whole slots. */
export const DURATION_CHOICES = Object.freeze([30, 60, 90, 120]);

export const LIMITS = Object.freeze({
  memberName: 80,
  zone: 64,
  maxMembers: MAX_MEMBERS,
  maxSerializedBytes: 32 * 1024,
});

/* -------------------------------------------------------------------------
   Small numeric helpers
   ---------------------------------------------------------------------- */

/** Always-positive modulo. `%` alone returns -30 for (-30 % 1440). */
export function mod(value, size) {
  return ((value % size) + size) % size;
}

/** 570 -> "09:30". Always zero-padded, always 24-hour. */
export function formatClock(minutesOfDay) {
  const m = mod(Math.round(minutesOfDay), MINUTES_PER_DAY);
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return `${String(h).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
}

/** 180 -> "3h 00m". The dock reading and every duration label use this. */
export function formatDuration(minutes) {
  const total = Math.max(0, Math.round(minutes));
  const h = Math.floor(total / 60);
  const m = total % 60;
  return `${h}h ${String(m).padStart(2, '0')}m`;
}

/** 0 -> "", 1 -> "+1 day", -2 -> "-2 days". */
export function formatDayShift(dayShift) {
  const d = Math.trunc(dayShift || 0);
  if (d === 0) return '';
  const magnitude = Math.abs(d);
  return `${d > 0 ? '+' : '-'}${magnitude} ${magnitude === 1 ? 'day' : 'days'}`;
}

/* -------------------------------------------------------------------------
   State
   ---------------------------------------------------------------------- */

export function makeMemberId() {
  return 'tz_' + Math.random().toString(36).slice(2, 10);
}

export function createDefaultState() {
  return {
    schema: SCHEMA_NAME,
    version: SCHEMA_VERSION,
    members: [],
    durationMinutes: 60,
    deadline: { weekday: 4, minutesOfDay: 1080, anchorMemberId: null },
  };
}

function clampSingleLine(value, maxLen) {
  if (typeof value !== 'string') return '';
  return value.replace(/\s*[\r\n]+\s*/g, ' ').trim().slice(0, maxLen);
}

function clampMinuteOfDay(value, fallback) {
  if (!Number.isFinite(value)) return fallback;
  // Snapped to the half-hour grid: a 09:17 start cannot be represented, and
  // silently keeping it would make the bar and the slot math disagree.
  const snapped = Math.round(value / SLOT_MINUTES) * SLOT_MINUTES;
  return mod(snapped, MINUTES_PER_DAY);
}

function sanitizeMember(raw, seen) {
  const r = raw && typeof raw === 'object' ? raw : {};
  const validId = typeof r.id === 'string' && /^tz_[a-z0-9]+$/i.test(r.id) && !seen.has(r.id);
  const id = validId ? r.id : makeMemberId();
  seen.add(id);

  const workStartMin = clampMinuteOfDay(r.workStartMin, DEFAULT_WORK_START_MIN);
  let workEndMin = clampMinuteOfDay(r.workEndMin, DEFAULT_WORK_END_MIN);
  // A zero-length band is never what anyone means by "my working hours", and
  // it would silently delete the member from every overlap. Give it an hour.
  if (workEndMin === workStartMin) workEndMin = mod(workStartMin + 60, MINUTES_PER_DAY);

  return {
    id,
    name: clampSingleLine(r.name, LIMITS.memberName),
    // Zone ids are IANA strings, never offsets: an offset saved today is
    // wrong the moment the member's region changes its clocks. See
    // "DST honesty" in the tool's methodology prose.
    zone: clampSingleLine(r.zone, LIMITS.zone),
    workStartMin,
    workEndMin,
  };
}

export function sanitizeState(raw) {
  const r = raw && typeof raw === 'object' ? raw : {};
  const seen = new Set();
  const members = (Array.isArray(r.members) ? r.members : [])
    .slice(0, LIMITS.maxMembers)
    .map((m) => sanitizeMember(m, seen));
  const memberIds = new Set(members.map((m) => m.id));

  const d = r.deadline && typeof r.deadline === 'object' ? r.deadline : {};
  const weekday = Number.isInteger(d.weekday) && d.weekday >= 0 && d.weekday <= 6 ? d.weekday : 4;
  const anchorMemberId = typeof d.anchorMemberId === 'string' && memberIds.has(d.anchorMemberId)
    ? d.anchorMemberId
    : (members[0] ? members[0].id : null);

  const duration = DURATION_CHOICES.includes(r.durationMinutes) ? r.durationMinutes : 60;

  return {
    schema: SCHEMA_NAME,
    version: SCHEMA_VERSION,
    members,
    durationMinutes: duration,
    deadline: {
      weekday,
      minutesOfDay: clampMinuteOfDay(d.minutesOfDay, 1080),
      anchorMemberId,
    },
  };
}

/**
 * Never throws. A corrupted or foreign payload returns a null state so the
 * caller can start fresh instead of crashing the planner.
 * @returns {{status: 'ok'|'malformed', state: object|null}}
 */
export function parseSavedState(raw) {
  let parsed = raw;
  if (typeof raw === 'string') {
    try {
      parsed = JSON.parse(raw);
    } catch {
      return { status: 'malformed', state: null };
    }
  }
  if (!parsed || typeof parsed !== 'object' || parsed.schema !== SCHEMA_NAME) {
    return { status: 'malformed', state: null };
  }
  return { status: 'ok', state: sanitizeState(parsed) };
}

export function serializeForSave(state) {
  const json = JSON.stringify(state);
  if (typeof TextEncoder !== 'undefined' && new TextEncoder().encode(json).length > LIMITS.maxSerializedBytes) {
    return null;
  }
  return json;
}

export function addMember(state, member) {
  if (state.members.length >= LIMITS.maxMembers) return state;
  const seen = new Set(state.members.map((m) => m.id));
  const next = sanitizeMember({ ...(member || {}), id: undefined }, seen);
  const members = [...state.members, next];
  return sanitizeState({
    ...state,
    members,
    deadline: {
      ...state.deadline,
      anchorMemberId: state.deadline.anchorMemberId || next.id,
    },
  });
}

export function removeMember(state, memberId) {
  const members = state.members.filter((m) => m.id !== memberId);
  return sanitizeState({
    ...state,
    members,
    deadline: {
      ...state.deadline,
      anchorMemberId: state.deadline.anchorMemberId === memberId ? null : state.deadline.anchorMemberId,
    },
  });
}

export function updateMember(state, memberId, patch) {
  return sanitizeState({
    ...state,
    members: state.members.map((m) => (m.id === memberId ? { ...m, ...(patch || {}) } : m)),
  });
}

/* -------------------------------------------------------------------------
   The half-hour UTC grid
   ---------------------------------------------------------------------- */

function allSlots() {
  return Array.from({ length: SLOTS_PER_DAY }, (_, i) => i);
}

/** Circular band length in minutes. 0 means an empty band. */
export function bandDuration(startLocalMin, endLocalMin) {
  return mod(endLocalMin - startLocalMin, MINUTES_PER_DAY);
}

/**
 * A local wall-clock band -> the UTC half-hour slots it covers.
 *
 * Wrap-midnight bands (22:00-06:00) are legal and normal - a member on the
 * far side of the world is often working across the UTC date boundary, and
 * refusing to model that would delete their availability entirely.
 *
 * @param {number} startLocalMin minutes past local midnight
 * @param {number} endLocalMin exclusive, may be <= start (wraps)
 * @param {number} offsetMinutes the member's UTC offset (local = UTC + offset)
 * @returns {number[]} slot indices in 0..47, in coverage order
 */
export function bandToSlots(startLocalMin, endLocalMin, offsetMinutes) {
  if (!Number.isFinite(offsetMinutes)) return [];
  const duration = bandDuration(startLocalMin, endLocalMin);
  if (duration <= 0) return [];
  if (duration >= MINUTES_PER_DAY) return allSlots();

  const utcStart = mod(startLocalMin - offsetMinutes, MINUTES_PER_DAY);
  // Snap inward: ceil the start, floor the end. A 45-minute-offset zone loses
  // at most a quarter hour on each end rather than gaining one.
  const firstSlot = Math.ceil(utcStart / SLOT_MINUTES);
  const lastSlotExclusive = Math.floor((utcStart + duration) / SLOT_MINUTES);

  const slots = [];
  for (let s = firstSlot; s < lastSlotExclusive && slots.length < SLOTS_PER_DAY; s++) {
    slots.push(mod(s, SLOTS_PER_DAY));
  }
  return slots;
}

/**
 * Contiguous circular runs from a slot list. `endSlot` is exclusive and may
 * exceed SLOTS_PER_DAY when the run wraps midnight - that is how a caller
 * tells a wrapping run from a normal one without re-deriving it.
 */
export function slotsToRuns(slots) {
  const present = new Array(SLOTS_PER_DAY).fill(false);
  for (const s of slots || []) {
    if (Number.isFinite(s)) present[mod(s, SLOTS_PER_DAY)] = true;
  }
  const count = present.reduce((acc, v) => acc + (v ? 1 : 0), 0);
  if (count === 0) return [];
  if (count === SLOTS_PER_DAY) {
    return [{ startSlot: 0, endSlot: SLOTS_PER_DAY, lengthSlots: SLOTS_PER_DAY, wraps: false }];
  }

  const runs = [];
  for (let s = 0; s < SLOTS_PER_DAY; s++) {
    if (!present[s] || present[mod(s - 1, SLOTS_PER_DAY)]) continue;
    let length = 0;
    while (present[mod(s + length, SLOTS_PER_DAY)]) length++;
    runs.push({ startSlot: s, endSlot: s + length, lengthSlots: length, wraps: s + length > SLOTS_PER_DAY });
  }
  return runs.sort((a, b) => a.startSlot - b.startSlot);
}

/**
 * Percentage-positioned spans for one run. A wrapping run yields TWO spans
 * (tail then head) so the bar can render it without a scroll container and
 * without a single fixed pixel.
 */
export function runToSpans(run) {
  const pctPerSlot = 100 / SLOTS_PER_DAY;
  if (!run || !run.lengthSlots) return [];
  if (run.endSlot <= SLOTS_PER_DAY) {
    return [{ startPct: run.startSlot * pctPerSlot, widthPct: run.lengthSlots * pctPerSlot }];
  }
  return [
    { startPct: run.startSlot * pctPerSlot, widthPct: (SLOTS_PER_DAY - run.startSlot) * pctPerSlot },
    { startPct: 0, widthPct: (run.endSlot - SLOTS_PER_DAY) * pctPerSlot },
  ];
}

function spansFor(slots) {
  return slotsToRuns(slots).flatMap(runToSpans);
}

/* -------------------------------------------------------------------------
   Member segments
   ---------------------------------------------------------------------- */

/**
 * Working band + stretch band per member, on the shared UTC grid.
 *
 * A member whose saved zone no longer resolves arrives here with
 * `offsetMinutes: null`. They are NOT dropped - they come back with
 * `resolved: false` so the UI can flag them for a re-pick - but every
 * computation below filters them out rather than guessing an offset.
 */
export function memberSegments(members) {
  return (Array.isArray(members) ? members : []).map((raw) => {
    const m = raw && typeof raw === 'object' ? raw : {};
    const base = {
      id: typeof m.id === 'string' ? m.id : '',
      name: typeof m.name === 'string' ? m.name : '',
      zone: typeof m.zone === 'string' ? m.zone : '',
      workStartMin: Number.isFinite(m.workStartMin) ? m.workStartMin : DEFAULT_WORK_START_MIN,
      workEndMin: Number.isFinite(m.workEndMin) ? m.workEndMin : DEFAULT_WORK_END_MIN,
    };

    if (!Number.isFinite(m.offsetMinutes)) {
      return {
        ...base,
        resolved: false,
        offsetMinutes: null,
        workSlots: [],
        stretchSlots: [],
        workSpans: [],
        stretchSpans: [],
        wrapsMidnight: false,
        workLabel: `${formatClock(base.workStartMin)}-${formatClock(base.workEndMin)}`,
        utcWorkLabel: '',
      };
    }

    const offsetMinutes = m.offsetMinutes;
    const workDuration = bandDuration(base.workStartMin, base.workEndMin);
    const workSlots = bandToSlots(base.workStartMin, base.workEndMin, offsetMinutes);

    const stretchStart = mod(base.workStartMin - STRETCH_EARLY_MINUTES, MINUTES_PER_DAY);
    const stretchDuration = workDuration + STRETCH_EARLY_MINUTES + STRETCH_LATE_MINUTES;
    // A core day long enough that the padding laps the clock has to become a
    // full 24h band explicitly - mod() would otherwise fold the end back
    // inside the start and produce a comically short "stretch".
    const stretchSlots = stretchDuration >= MINUTES_PER_DAY
      ? allSlots()
      : bandToSlots(stretchStart, mod(stretchStart + stretchDuration, MINUTES_PER_DAY), offsetMinutes);

    const utcStart = mod(base.workStartMin - offsetMinutes, MINUTES_PER_DAY);
    const utcEnd = mod(utcStart + workDuration, MINUTES_PER_DAY);

    return {
      ...base,
      resolved: true,
      offsetMinutes,
      workDuration,
      workSlots,
      stretchSlots,
      workSpans: spansFor(workSlots),
      stretchSpans: spansFor(stretchSlots),
      wrapsMidnight: base.workEndMin <= base.workStartMin,
      workLabel: `${formatClock(base.workStartMin)}-${formatClock(base.workEndMin)}`,
      utcWorkLabel: `${formatClock(utcStart)}-${formatClock(utcEnd)} UTC`,
    };
  });
}

function resolvedSegments(members) {
  return memberSegments(members).filter((s) => s.resolved);
}

export function unresolvedMemberIds(members) {
  return memberSegments(members).filter((s) => !s.resolved).map((s) => s.id);
}

/* -------------------------------------------------------------------------
   Spread and overlap
   ---------------------------------------------------------------------- */

/**
 * The CIRCULAR spread of a set of UTC offsets: the width of the smallest arc
 * on the 24-hour clock that contains all of them.
 *
 * Linear max-minus-min is the intuitive version and it is wrong. Chicago
 * (-06:00) and Singapore (+08:00) are 14 hours apart linearly, which predicts
 * no overlap at all inside a 13-hour acceptance window - yet both teams can
 * genuinely meet from 00:00-03:00 UTC. The circular spread is 10 hours, and
 * 13 - 10 = 3 hours, which is the real answer.
 */
export function spreadMinutes(offsets) {
  const normalized = (Array.isArray(offsets) ? offsets : [])
    .filter(Number.isFinite)
    .map((o) => mod(o, MINUTES_PER_DAY))
    .sort((a, b) => a - b);
  const unique = [...new Set(normalized)];
  if (unique.length < 2) return 0;

  let largestGap = 0;
  for (let i = 0; i < unique.length; i++) {
    const isLast = i === unique.length - 1;
    const gap = isLast
      ? unique[0] + MINUTES_PER_DAY - unique[i]
      : unique[i + 1] - unique[i];
    if (gap > largestGap) largestGap = gap;
  }
  return MINUTES_PER_DAY - largestGap;
}

/** Coarse analytics bucket. Floor, so "12h30m apart" reports as 10-12. */
export function spreadHoursBucket(minutes) {
  const hours = Math.floor(Math.max(0, minutes || 0) / 60);
  if (hours <= 3) return '0-3';
  if (hours <= 6) return '4-6';
  if (hours <= 9) return '7-9';
  if (hours <= 12) return '10-12';
  return '13+';
}

function localTimeFor(utcMinutes, offsetMinutes) {
  const raw = utcMinutes + offsetMinutes;
  return {
    minutesOfDay: mod(raw, MINUTES_PER_DAY),
    dayShift: Math.floor(raw / MINUTES_PER_DAY),
  };
}

/**
 * The intersection of every resolved member's WORKING band, as zero or more
 * circular windows, plus the circular spread of the team.
 *
 * Two windows is a real result, not a bug: London and Auckland can both meet
 * early-morning-UTC and late-evening-UTC, and collapsing that to one number
 * would hide half the team's options.
 */
export function overlapWindows(members) {
  const segments = memberSegments(members);
  const resolved = segments.filter((s) => s.resolved);
  const unresolvedIds = segments.filter((s) => !s.resolved).map((s) => s.id);
  const spread = spreadMinutes(resolved.map((s) => s.offsetMinutes));

  if (resolved.length === 0) {
    return {
      windows: [], hasOverlap: false, spreadMinutes: 0, totalOverlapMinutes: 0,
      longestWindowMinutes: 0, memberCount: 0, unresolvedIds, spans: [],
    };
  }

  const present = new Array(SLOTS_PER_DAY).fill(true);
  for (const segment of resolved) {
    const set = new Set(segment.workSlots);
    for (let i = 0; i < SLOTS_PER_DAY; i++) {
      if (!set.has(i)) present[i] = false;
    }
  }
  const sharedSlots = [];
  for (let i = 0; i < SLOTS_PER_DAY; i++) if (present[i]) sharedSlots.push(i);

  const windows = slotsToRuns(sharedSlots).map((run) => {
    const startUtcMin = run.startSlot * SLOT_MINUTES;
    const durationMinutes = run.lengthSlots * SLOT_MINUTES;
    return {
      startSlot: run.startSlot,
      endSlot: run.endSlot,
      startUtcMin,
      endUtcMin: mod(startUtcMin + durationMinutes, MINUTES_PER_DAY),
      durationMinutes,
      wraps: run.wraps,
      spans: runToSpans(run),
      perMember: resolved.map((s) => {
        const start = localTimeFor(startUtcMin, s.offsetMinutes);
        const end = localTimeFor(startUtcMin + durationMinutes, s.offsetMinutes);
        const startMarker = formatDayShift(start.dayShift);
        const endMarker = formatDayShift(end.dayShift);
        // A window can cross a member's local midnight without both ends landing on the
        // same day relative to the reference day - one suffix on the whole range would
        // silently apply the start's day to the end's clock time too (or vice versa), which
        // is exactly the "different calendar day" confusion this tool exists to prevent.
        // Each endpoint gets its own marker unless they genuinely agree.
        const label = startMarker === endMarker
          ? `${formatClock(start.minutesOfDay)}-${formatClock(end.minutesOfDay)}${startMarker ? ` (${startMarker})` : ''}`
          : `${formatClock(start.minutesOfDay)}${startMarker ? ` (${startMarker})` : ''}-${formatClock(end.minutesOfDay)}${endMarker ? ` (${endMarker})` : ''}`;
        return {
          id: s.id,
          name: s.name,
          zone: s.zone,
          startMinutesOfDay: start.minutesOfDay,
          endMinutesOfDay: end.minutesOfDay,
          label,
          startDayShift: start.dayShift,
          endDayShift: end.dayShift,
        };
      }),
    };
  }).sort((a, b) => b.durationMinutes - a.durationMinutes || a.startSlot - b.startSlot);

  const totalOverlapMinutes = sharedSlots.length * SLOT_MINUTES;
  return {
    windows,
    hasOverlap: windows.length > 0,
    spreadMinutes: spread,
    totalOverlapMinutes,
    longestWindowMinutes: windows.length ? windows[0].durationMinutes : 0,
    memberCount: resolved.length,
    unresolvedIds,
    spans: windows.flatMap((w) => w.spans),
  };
}

/* -------------------------------------------------------------------------
   Meeting slots
   ---------------------------------------------------------------------- */

/**
 * Every one of the 48 possible start slots, scored by PAIN: the total number
 * of meeting minutes that fall outside somebody's working hours. Each entry
 * names who eats that pain, because "the majority zone proposes a time
 * convenient for the majority zone" is exactly the silent default this tool
 * exists to make visible.
 *
 * `viable` means every member is at least inside their stretch band for the
 * whole meeting - i.e. it costs somebody their evening, not their sleep.
 */
export function rankedMeetingSlots(members, durationMinutes = 60) {
  const resolved = resolvedSegments(members);
  if (resolved.length === 0) return [];

  const requested = Number.isFinite(durationMinutes) ? durationMinutes : 60;
  const durationSlots = Math.max(1, Math.min(SLOTS_PER_DAY, Math.ceil(requested / SLOT_MINUTES)));
  const actualDuration = durationSlots * SLOT_MINUTES;

  const workSets = resolved.map((s) => new Set(s.workSlots));
  const stretchSets = resolved.map((s) => new Set(s.stretchSlots));

  const slots = [];
  for (let start = 0; start < SLOTS_PER_DAY; start++) {
    const startUtcMin = start * SLOT_MINUTES;

    const perMember = resolved.map((segment, i) => {
      let painMinutes = 0;
      let outsideStretchMinutes = 0;
      for (let k = 0; k < durationSlots; k++) {
        const slot = mod(start + k, SLOTS_PER_DAY);
        if (!workSets[i].has(slot)) painMinutes += SLOT_MINUTES;
        if (!stretchSets[i].has(slot)) outsideStretchMinutes += SLOT_MINUTES;
      }
      const local = localTimeFor(startUtcMin, segment.offsetMinutes);
      const localEnd = localTimeFor(startUtcMin + actualDuration, segment.offsetMinutes);
      return {
        id: segment.id,
        name: segment.name,
        zone: segment.zone,
        painMinutes,
        outsideStretchMinutes,
        startMinutesOfDay: local.minutesOfDay,
        endMinutesOfDay: localEnd.minutesOfDay,
        label: `${formatClock(local.minutesOfDay)}-${formatClock(localEnd.minutesOfDay)}`,
        dayShift: local.dayShift,
      };
    });

    const painMinutes = perMember.reduce((acc, p) => acc + p.painMinutes, 0);
    const outsideStretchMinutes = perMember.reduce((acc, p) => acc + p.outsideStretchMinutes, 0);
    const worstMemberPain = perMember.reduce((acc, p) => Math.max(acc, p.painMinutes), 0);
    const eaters = perMember
      .filter((p) => p.painMinutes > 0)
      .slice()
      .sort((a, b) => b.painMinutes - a.painMinutes || String(a.name).localeCompare(String(b.name)));

    slots.push({
      startSlot: start,
      startUtcMin,
      endUtcMin: mod(startUtcMin + actualDuration, MINUTES_PER_DAY),
      durationMinutes: actualDuration,
      painMinutes,
      outsideStretchMinutes,
      worstMemberPain,
      viable: outsideStretchMinutes === 0,
      perMember,
      eaters,
    });
  }

  // Least total pain first; then least sleep-costing; then the slot whose
  // worst-hit person is hit least (fairness beats a lower total that lands
  // entirely on one head); then slot order, purely for determinism.
  return slots.sort((a, b) =>
    a.painMinutes - b.painMinutes ||
    a.outsideStretchMinutes - b.outsideStretchMinutes ||
    a.worstMemberPain - b.worstMemberPain ||
    a.startSlot - b.startSlot);
}

/* -------------------------------------------------------------------------
   Rotation
   ---------------------------------------------------------------------- */

function lexLess(a, b) {
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return a[i] < b[i];
  }
  return false;
}

function exemptionAlternative(slot, headline) {
  const target = slot && slot.eaters && slot.eaters.length ? slot.eaters[0] : null;
  const who = target && target.name ? target.name : 'Whoever is furthest outside the window';
  return {
    memberId: target ? target.id : null,
    memberName: target ? target.name : '',
    headline,
    // Straight from the article: if rotation is impossible, pay in a
    // different currency rather than pretending the cost isn't there.
    steps: [
      `${who} is exempt from the live window entirely - not asked to take a call outside their day every week.`,
      `${who} gets a written brief within the hour, not "notes eventually".`,
      `${who} owns a decision the meeting is not allowed to make without them.`,
      `Every open question sent to ${who} carries the default you will take if nobody answers, so one round trip never costs a day.`,
    ],
  };
}

/**
 * Greedy cumulative-pain balancing across `rounds` meetings.
 *
 * Each round picks the candidate slot that minimises (worst cumulative pain
 * so far, then the sum of squares, then the round's own pain, then slot
 * order). Minimising the max is what actually moves the antisocial slot
 * around instead of letting one person absorb it every week.
 *
 * When rotation genuinely cannot help, this says so rather than dressing a
 * single fixed slot up as a rotation:
 *  - 'no-pain'     every candidate is inside everyone's working hours.
 *  - 'no-slot'     nothing fits inside everyone's stretch hours at all.
 *  - 'single-slot' exactly one viable slot exists, and it costs somebody.
 * The last two carry the article's exemption/handoff alternative.
 */
export function rotationPlan(slots, members, options = {}) {
  const rounds = Number.isInteger(options.rounds) && options.rounds > 0 ? options.rounds : 4;
  const resolved = resolvedSegments(members);
  const ranked = Array.isArray(slots) ? slots : [];

  const empty = {
    kind: 'no-members', rounds: [], cumulative: [], balanceGapMinutes: 0,
    candidateCount: 0, alternative: null,
  };
  if (resolved.length === 0 || ranked.length === 0) return empty;

  const viable = ranked.filter((s) => s.viable);
  const painless = viable.find((s) => s.painMinutes === 0);
  if (painless) {
    return {
      kind: 'no-pain',
      rounds: [{
        round: 1,
        startSlot: painless.startSlot,
        startUtcMin: painless.startUtcMin,
        durationMinutes: painless.durationMinutes,
        painMinutes: 0,
        eaters: [],
      }],
      cumulative: resolved.map((s) => ({ id: s.id, name: s.name, painMinutes: 0 })),
      balanceGapMinutes: 0,
      candidateCount: viable.length,
      alternative: null,
    };
  }

  if (viable.length === 0) {
    return {
      kind: 'no-slot',
      rounds: [],
      cumulative: resolved.map((s) => ({ id: s.id, name: s.name, painMinutes: 0 })),
      balanceGapMinutes: 0,
      candidateCount: 0,
      alternative: exemptionAlternative(
        ranked[0],
        'There is no time the whole team can meet inside anyone\'s stretch hours. Rotation cannot fix this one.'),
    };
  }

  if (viable.length === 1) {
    return {
      kind: 'single-slot',
      rounds: [{
        round: 1,
        startSlot: viable[0].startSlot,
        startUtcMin: viable[0].startUtcMin,
        durationMinutes: viable[0].durationMinutes,
        painMinutes: viable[0].painMinutes,
        eaters: viable[0].eaters.map((e) => ({ id: e.id, name: e.name, painMinutes: e.painMinutes })),
      }],
      cumulative: resolved.map((s) => {
        const hit = viable[0].perMember.find((p) => p.id === s.id);
        return { id: s.id, name: s.name, painMinutes: hit ? hit.painMinutes : 0 };
      }),
      balanceGapMinutes: viable[0].worstMemberPain,
      candidateCount: 1,
      alternative: exemptionAlternative(
        viable[0],
        'Only one slot works for the whole team, so there is nothing to rotate between.'),
    };
  }

  // Every viable slot is a candidate, not just the globally-cheapest few: a top-N-by-total-pain
  // prefilter can leave the greedy fairness pass with nothing but slots that all burden the same
  // person, which defeats the whole point of rotating. The grid caps at 48 half-hour slots, so
  // there's no performance reason to prefilter before the (candidates x members) comparison below.
  const candidates = viable;
  const cumulative = new Map(resolved.map((s) => [s.id, 0]));
  const roundsOut = [];

  for (let r = 0; r < rounds; r++) {
    let best = null;
    for (const slot of candidates) {
      const after = new Map(cumulative);
      for (const p of slot.perMember) after.set(p.id, (after.get(p.id) || 0) + p.painMinutes);
      const values = [...after.values()];
      const key = [
        values.reduce((acc, v) => Math.max(acc, v), 0),
        values.reduce((acc, v) => acc + v * v, 0),
        slot.painMinutes,
        slot.startSlot,
      ];
      if (!best || lexLess(key, best.key)) best = { slot, key, after };
    }
    if (!best) break;
    for (const [id, value] of best.after) cumulative.set(id, value);
    roundsOut.push({
      round: r + 1,
      startSlot: best.slot.startSlot,
      startUtcMin: best.slot.startUtcMin,
      durationMinutes: best.slot.durationMinutes,
      painMinutes: best.slot.painMinutes,
      eaters: best.slot.eaters.map((e) => ({ id: e.id, name: e.name, painMinutes: e.painMinutes })),
    });
  }

  const cumulativeOut = resolved.map((s) => ({
    id: s.id, name: s.name, painMinutes: cumulative.get(s.id) || 0,
  }));
  const values = cumulativeOut.map((c) => c.painMinutes);

  return {
    kind: 'rotating',
    rounds: roundsOut,
    cumulative: cumulativeOut,
    balanceGapMinutes: Math.max(...values) - Math.min(...values),
    candidateCount: candidates.length,
    alternative: null,
  };
}

/* -------------------------------------------------------------------------
   Deadline translation
   ---------------------------------------------------------------------- */

/**
 * One anchor member's wall time, rendered in every member's local time with
 * explicit +1 day / -1 day markers.
 *
 * The day marker is the entire point. "Friday 23:59 in Singapore is Friday
 * 11:59 in New York" is the confusion that actually costs teams a submission,
 * and it is never about the hour - it is that the date rolled over for some
 * people and not others.
 */
export function translateDeadline(deadline, members) {
  const d = deadline && typeof deadline === 'object' ? deadline : {};
  const segments = memberSegments(members);
  const resolved = segments.filter((s) => s.resolved);
  const unresolvedIds = segments.filter((s) => !s.resolved).map((s) => s.id);

  const anchor = resolved.find((s) => s.id === d.anchorMemberId) || resolved[0] || null;
  if (!anchor) {
    return { anchor: null, weekday: 0, minutesOfDay: 0, utcLabel: '', rows: [], unresolvedIds };
  }

  const weekday = Number.isInteger(d.weekday) && d.weekday >= 0 && d.weekday <= 6 ? d.weekday : 4;
  const minutesOfDay = clampMinuteOfDay(d.minutesOfDay, 1080);

  const anchorWeekMinutes = weekday * MINUTES_PER_DAY + minutesOfDay;
  const utcWeekMinutes = anchorWeekMinutes - anchor.offsetMinutes;

  const rows = resolved.map((s) => {
    const raw = utcWeekMinutes + s.offsetMinutes;
    const local = mod(raw, MINUTES_PER_WEEK);
    const localWeekday = Math.floor(local / MINUTES_PER_DAY);
    const localMinutes = local % MINUTES_PER_DAY;
    const dayShift = Math.floor(raw / MINUTES_PER_DAY) - weekday;
    return {
      id: s.id,
      name: s.name,
      zone: s.zone,
      weekday: localWeekday,
      weekdayLabel: WEEKDAYS[localWeekday],
      minutesOfDay: localMinutes,
      timeLabel: formatClock(localMinutes),
      dayShift,
      dayShiftLabel: formatDayShift(dayShift),
      isAnchor: s.id === anchor.id,
    };
  });

  const utcRaw = mod(utcWeekMinutes, MINUTES_PER_WEEK);
  return {
    anchor: { id: anchor.id, name: anchor.name, zone: anchor.zone },
    weekday,
    minutesOfDay,
    utcLabel: `${WEEKDAYS[Math.floor(utcRaw / MINUTES_PER_DAY)]} ${formatClock(utcRaw % MINUTES_PER_DAY)} UTC`,
    rows,
    unresolvedIds,
  };
}

/* -------------------------------------------------------------------------
   DST honesty
   ---------------------------------------------------------------------- */

/**
 * Compares offsets resolved at two instants (the UI uses "now" and
 * "now + 7 days") and reports which members' clocks move in between.
 *
 * Pure on purpose: the two offset sets come from the Intl adapter, so this
 * can be tested with literal offsets captured from a DST-split date without
 * a clock anywhere in the test.
 */
export function detectOffsetChanges(nowMembers, laterMembers) {
  const later = new Map(
    (Array.isArray(laterMembers) ? laterMembers : [])
      .filter((m) => m && typeof m === 'object')
      .map((m) => [m.id, m]));

  const changes = [];
  for (const m of Array.isArray(nowMembers) ? nowMembers : []) {
    if (!m || typeof m !== 'object') continue;
    const other = later.get(m.id);
    if (!other) continue;
    if (!Number.isFinite(m.offsetMinutes) || !Number.isFinite(other.offsetMinutes)) continue;
    if (m.offsetMinutes === other.offsetMinutes) continue;
    changes.push({
      id: m.id,
      name: m.name || '',
      zone: m.zone || '',
      fromMinutes: m.offsetMinutes,
      toMinutes: other.offsetMinutes,
      deltaMinutes: other.offsetMinutes - m.offsetMinutes,
    });
  }
  return { changed: changes.length > 0, changes };
}

/* -------------------------------------------------------------------------
   Derived summary + markdown
   ---------------------------------------------------------------------- */

/** Everything the UI renders, from one call, so nothing can drift. */
export function computePlan(state, resolvedMembers) {
  const members = Array.isArray(resolvedMembers) ? resolvedMembers : [];
  const segments = memberSegments(members);
  const overlap = overlapWindows(members);
  const slots = rankedMeetingSlots(members, state ? state.durationMinutes : 60);
  const rotation = rotationPlan(slots, members);
  const deadline = translateDeadline(state ? state.deadline : null, members);
  return { segments, overlap, slots, rotation, deadline };
}

/** Coarse, non-identifying analytics params. Never a zone id, never a name. */
export function analyticsParams(computed) {
  const overlap = computed && computed.overlap ? computed.overlap : { memberCount: 0, hasOverlap: false, spreadMinutes: 0 };
  return {
    member_count: overlap.memberCount,
    has_overlap: overlap.hasOverlap,
    spread_hours_bucket: spreadHoursBucket(overlap.spreadMinutes),
  };
}

/** The dock reading, e.g. "3 members · 3h 00m shared". */
export function dockReading(computed) {
  const overlap = computed && computed.overlap ? computed.overlap : null;
  if (!overlap || overlap.memberCount === 0) return 'Add a teammate to start';
  const people = `${overlap.memberCount} member${overlap.memberCount === 1 ? '' : 's'}`;
  if (!overlap.hasOverlap) return `${people} · no shared window`;
  return `${people} · ${formatDuration(overlap.longestWindowMinutes)} shared`;
}

function eaterSentence(eaters) {
  if (!eaters || eaters.length === 0) return 'Inside everyone\'s working hours.';
  return eaters
    .map((e) => `${e.name || 'Unnamed'} (${formatDuration(e.painMinutes)} outside their working hours)`)
    .join('; ');
}

/**
 * The plan as markdown. Every user-controlled string goes through
 * escapeMarkdown (shared with the charter builder) so a member named
 * "# Fake Heading" stays a literal string and can never smuggle raw HTML
 * through a markdown-to-HTML render.
 */
export function buildMarkdown(state, computed) {
  const s = state || createDefaultState();
  const c = computed || { segments: [], overlap: { windows: [] }, slots: [], rotation: { rounds: [] }, deadline: { rows: [] } };
  const lines = [];

  lines.push('# Team time-zone plan');
  lines.push('');

  lines.push('## Members');
  lines.push('');
  if (!c.segments.length) {
    lines.push('- [Add team members]');
  } else {
    for (const seg of c.segments) {
      const name = escapeMarkdown(seg.name || '[Unnamed member]');
      const zone = escapeMarkdown(seg.zone || '[no time zone]');
      if (!seg.resolved) {
        lines.push(`- ${name} — ${zone}: time zone could not be resolved, excluded from the math. Pick it again.`);
      } else {
        lines.push(`- ${name} — ${zone}: works ${seg.workLabel} local (${seg.utcWorkLabel})`);
      }
    }
  }
  lines.push('');

  lines.push('## Shared window');
  lines.push('');
  const overlap = c.overlap || {};
  lines.push(`Circular spread across the team: ${formatDuration(overlap.spreadMinutes || 0)}.`);
  lines.push('');
  if (!overlap.windows || overlap.windows.length === 0) {
    lines.push('There is no window where every member is inside their working hours at the same time. Run this team on handoffs, not meetings.');
    lines.push('');
  } else {
    for (const w of overlap.windows) {
      lines.push(`- ${formatClock(w.startUtcMin)}-${formatClock(w.endUtcMin)} UTC (${formatDuration(w.durationMinutes)})`);
      for (const p of w.perMember) {
        // p.label already carries its own day marker(s) - see overlapWindows above.
        lines.push(`  - ${escapeMarkdown(p.name || '[Unnamed member]')}: ${p.label}`);
      }
    }
    lines.push('');
  }

  lines.push('## Best meeting slots');
  lines.push('');
  const top = (c.slots || []).slice(0, 5);
  if (!top.length) {
    lines.push('[Add team members to rank meeting slots]');
    lines.push('');
  } else {
    for (const slot of top) {
      lines.push(`- ${formatClock(slot.startUtcMin)}-${formatClock(slot.endUtcMin)} UTC — ${formatDuration(slot.painMinutes)} of pain. ${escapeMarkdown(eaterSentence(slot.eaters))}`);
    }
    lines.push('');
  }

  lines.push('## Rotation');
  lines.push('');
  const rotation = c.rotation || {};
  if (rotation.kind === 'no-pain') {
    lines.push('Every shared slot sits inside everyone\'s working hours. Pick one, publish it, and stop negotiating.');
    lines.push('');
  } else if (rotation.kind === 'rotating') {
    lines.push('Rotate the antisocial slot so the same person is not paying for it every week:');
    lines.push('');
    for (const r of rotation.rounds) {
      lines.push(`- Week ${r.round}: ${formatClock(r.startUtcMin)} UTC — ${escapeMarkdown(eaterSentence(r.eaters))}`);
    }
    lines.push('');
    lines.push('Cumulative cost over that cycle:');
    for (const c2 of rotation.cumulative) {
      lines.push(`- ${escapeMarkdown(c2.name || '[Unnamed member]')}: ${formatDuration(c2.painMinutes)}`);
    }
    lines.push('');
  } else if (rotation.alternative) {
    lines.push(escapeMarkdown(rotation.alternative.headline));
    lines.push('');
    lines.push('Pay in a different currency instead:');
    for (const step of rotation.alternative.steps) lines.push(`- ${escapeMarkdown(step)}`);
    lines.push('');
  } else {
    lines.push('[Add team members to build a rotation]');
    lines.push('');
  }

  lines.push('## Deadline in every local time');
  lines.push('');
  const deadline = c.deadline || {};
  if (!deadline.rows || deadline.rows.length === 0) {
    lines.push('[Pick an anchor member to translate a deadline]');
    lines.push('');
  } else {
    lines.push(`Anchor: ${escapeMarkdown(deadline.anchor ? (deadline.anchor.name || '[Unnamed member]') : '')} — ${WEEKDAYS[deadline.weekday]} ${formatClock(deadline.minutesOfDay)} local (${deadline.utcLabel}).`);
    lines.push('');
    for (const row of deadline.rows) {
      const marker = row.dayShiftLabel ? ` (${row.dayShiftLabel})` : '';
      lines.push(`- ${escapeMarkdown(row.name || '[Unnamed member]')}: ${row.weekdayLabel} ${row.timeLabel}${marker}`);
    }
    lines.push('');
    lines.push('Always write the day as well as the hour - for part of the team the deadline lands on a different calendar date than the one you typed.');
    lines.push('');
  }

  lines.push('## End-of-day handoff note');
  lines.push('');
  lines.push('- What I finished, with the actual link.');
  lines.push('- What is next and who owns it, named.');
  lines.push('- The decisions I already made, with the assumption stated out loud.');
  lines.push('- Every open question paired with the default I will take if nobody answers.');
  lines.push('');

  lines.push('## Method');
  lines.push('');
  lines.push('Overlap is computed on a half-hour grid from each member\'s own working hours, converted to UTC using the offset their time zone is on this week.');
  lines.push('Because clocks change, these windows are computed for the current week only - re-run this plan after any daylight saving change.');
  lines.push('');

  lines.push('---');
  lines.push('*Built with the free MBA Lab team time-zone overlap planner - mbalab.net/tools/team-time-zone-planner*');

  return lines.join('\n');
}
